import { useEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { motion, AnimatePresence } from 'framer-motion'

// Layout
import ScrollContainer from './components/layout/ScrollContainer'
import ProgressBar from './components/ui/ProgressBar'
import AudioControls from './components/ui/AudioControls'

// Hooks
import useAutoScroll from './hooks/useAutoScroll'

// Sections
import HeroSection from './components/sections/HeroSection'
import DebutsSection from './components/sections/DebutsSection'
import IntegrationSection from './components/sections/IntegrationSection'
import ChorusSection from './components/sections/ChorusSection'
import EteSection from './components/sections/EteSection'
import AutomneSection from './components/sections/AutomneSection'
import StatsSection from './components/sections/StatsSection'
import EpreuvesSection from './components/sections/EpreuvesSection'
import CelebrationSection from './components/sections/CelebrationSection'
import PhotoGallerySection from './components/sections/PhotoGallerySection'
import FuturSection from './components/sections/FuturSection'
import ConclusionSection from './components/sections/ConclusionSection'

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger)

export default function App() {
  const {
    isAutoScrolling,
    isPaused,
    togglePause,
    speedLabel,
    speedIndex,
    maxSpeedIndex,
    increaseSpeed,
    decreaseSpeed
  } = useAutoScroll({
    idleTimeout: 8000, // 8 seconds before auto-scroll starts
    initialSpeedIndex: 1, // Default to 1x speed
    enabled: true
  })

  useEffect(() => {
    // Refresh ScrollTrigger after all content is loaded
    const timer = setTimeout(() => {
      ScrollTrigger.refresh()
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="relative bg-dark-primary min-h-screen">
      {/* Progress Bar */}
      <ProgressBar />

      {/* Audio Controls - Child of God by BLXT */}
      <AudioControls audioSrc="/audio/child-of-god.mp3" />

      {/* Auto-scroll Controls */}
      <AutoScrollIndicator
        isAutoScrolling={isAutoScrolling}
        isPaused={isPaused}
        onToggle={togglePause}
        speedLabel={speedLabel}
        speedIndex={speedIndex}
        maxSpeedIndex={maxSpeedIndex}
        onSpeedUp={increaseSpeed}
        onSpeedDown={decreaseSpeed}
      />

      {/* Main Content */}
      <ScrollContainer>
        {/* Hero */}
        <HeroSection />

        {/* Verse 1 - Les Débuts */}
        <DebutsSection />

        {/* Verse 2 - L'Intégration */}
        <IntegrationSection />

        {/* Chorus - Le Moment Clé (4 juin) */}
        <ChorusSection />

        {/* Verse 3 - Notre Été */}
        <EteSection />

        {/* Verse 4 - L'Automne */}
        <AutomneSection />

        {/* Stats Interlude */}
        <StatsSection />

        {/* Bridge - Les Épreuves */}
        <EpreuvesSection />

        {/* Verse 5 - Célébration */}
        <CelebrationSection />

        {/* Photo Gallery */}
        <PhotoGallerySection />

        {/* Outro - Le Futur */}
        <FuturSection />

        {/* Conclusion */}
        <ConclusionSection />
      </ScrollContainer>

      {/* Navigation Dots (optional) */}
      <NavigationDots />
    </div>
  )
}

function AutoScrollIndicator({ isAutoScrolling, isPaused, onToggle, speedLabel, speedIndex, maxSpeedIndex, onSpeedUp, onSpeedDown }) {
  return (
    <motion.div
      className="fixed bottom-6 left-6 z-40 flex flex-col gap-2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 2 }}
    >
      {/* Main controls row */}
      <div className="flex items-center gap-2">
        {/* Play/Pause Button */}
        <motion.button
          onClick={onToggle}
          className={`flex items-center gap-2 px-3 py-2 rounded-full backdrop-blur-sm border transition-all duration-300 ${
            isPaused
              ? 'bg-white/10 border-white/20 text-white/70'
              : 'bg-primary-pink/20 border-primary-pink/30 text-primary-pink'
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title={isPaused ? 'Activer le défilement auto' : 'Pause le défilement auto'}
        >
          <span className="text-base">
            {isPaused ? '▶️' : '⏸️'}
          </span>
        </motion.button>

        {/* Speed Controls */}
        <div className="flex items-center gap-1 px-2 py-1.5 rounded-full backdrop-blur-sm bg-white/5 border border-white/10">
          {/* Decrease Speed */}
          <motion.button
            onClick={onSpeedDown}
            className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
              speedIndex <= 0
                ? 'text-white/30 cursor-not-allowed'
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            whileHover={speedIndex > 0 ? { scale: 1.1 } : {}}
            whileTap={speedIndex > 0 ? { scale: 0.9 } : {}}
            title="Ralentir"
            disabled={speedIndex <= 0}
          >
            <span className="text-sm">➖</span>
          </motion.button>

          {/* Speed Display */}
          <motion.span
            className="px-2 text-xs font-bold text-primary-pink min-w-[40px] text-center"
            key={speedLabel}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500 }}
          >
            {speedLabel}
          </motion.span>

          {/* Increase Speed */}
          <motion.button
            onClick={onSpeedUp}
            className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
              speedIndex >= maxSpeedIndex
                ? 'text-white/30 cursor-not-allowed'
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            whileHover={speedIndex < maxSpeedIndex ? { scale: 1.1 } : {}}
            whileTap={speedIndex < maxSpeedIndex ? { scale: 0.9 } : {}}
            title="Accélérer"
            disabled={speedIndex >= maxSpeedIndex}
          >
            <span className="text-sm">➕</span>
          </motion.button>
        </div>

        {/* Auto-scroll indicator */}
        <AnimatePresence>
          {isAutoScrolling && !isPaused && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary-pink/20 rounded-full border border-primary-pink/30"
            >
              {/* Animated dots */}
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 h-1.5 bg-primary-pink rounded-full"
                    animate={{
                      y: [0, -4, 0],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 0.8,
                      repeat: Infinity,
                      delay: i * 0.15
                    }}
                  />
                ))}
              </div>
              <span className="text-xs text-primary-pink hidden sm:inline">
                Auto
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

function NavigationDots() {
  const sections = [
    { id: 'hero', label: 'Intro', emoji: '💕' },
    { id: 'debuts', label: 'Débuts', emoji: '💬' },
    { id: 'integration', label: 'Intégration', emoji: '👨‍👩‍👧‍👦' },
    { id: 'chorus', label: '4 Juin', emoji: '❤️' },
    { id: 'ete', label: 'Été', emoji: '☀️' },
    { id: 'automne', label: 'Automne', emoji: '🍂' },
    { id: 'stats', label: 'Chiffres', emoji: '📊' },
    { id: 'epreuves', label: 'Épreuves', emoji: '💪' },
    { id: 'celebration', label: 'Célébration', emoji: '🎓' },
    { id: 'photos', label: 'Photos', emoji: '📸' },
    { id: 'futur', label: 'Futur', emoji: '🔮' },
    { id: 'conclusion', label: 'Fin', emoji: '🙏' }
  ]

  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <nav className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:block">
      <div className="flex flex-col gap-3">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className="group relative w-3 h-3 rounded-full bg-white/20 hover:bg-primary-pink transition-colors"
            title={section.label}
          >
            {/* Tooltip */}
            <span className="absolute right-full mr-3 px-2 py-1 text-xs bg-dark-primary/90 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {section.emoji} {section.label}
            </span>
          </button>
        ))}
      </div>
    </nav>
  )
}
